import Vue from 'vue'
import VueRouter from 'vue-router'
import store from './store'
import { startNavProgress, stopNavProgress } from './services/fast-loading'

import frontendRoutes from '@routers/frontend';
import guestRoutes from '@routers/guest';
import authRoutes from '@routers/auth';
import authWithAcademicSessionRoutes from '@routers/authWithAcademicSession';
import authMeeting from '@routers/authMeeting';
import authSecurityRoutes from '@routers/authSecurity';
import errorRoutes from '@routers/error';

const defaultPage = require('@layouts/default-page').default;

let routes = [
    {
        path: '/',                      // all the routes which can be access without authentication
        component: () => import(/* webpackChunkName: "js/guestPage" */ '@layouts/guest-page'),
        meta: { validate: ['is_guest'] },
        children: [
            ...guestRoutes,
        ]
    },
    {
        path: '/',                      // all the routes which can be access without authentication
        component: () => import(/* webpackChunkName: "js/guestPage" */ '@layouts/guest-page'),
        meta: { validate: [] },
        children: [
            ...frontendRoutes,
        ]
    },
    {
        path: '/',                                       // all the routes which needs authentication + two factor authentication + lock screen
        component: defaultPage,
        meta: { validate: ['is_auth','two_factor_security','is_screen_locked','valid_subscription'] },
        children: [
            ...authRoutes,
        ]
    },
    {
        path: '/',                                       // all the routes which needs authentication + two factor authentication + lock screen
        component: defaultPage,
        meta: { validate: ['is_auth','two_factor_security','is_screen_locked','valid_subscription','has_academic_session'] },
        children: [
            ...authWithAcademicSessionRoutes,
        ]
    },
    {
        path: '/',                                       // all the routes which needs authentication + two factor authentication + lock screen
        component: () => import(/* webpackChunkName: "js/meetingPage" */ '@layouts/meeting-page'),
        meta: { validate: ['is_auth','two_factor_security','is_screen_locked','has_academic_session'] },
        children: [
            ...authMeeting,
        ]
    },
    {
        path: '/',                                      // all the routes which needs authentication + two factor authentication + lock screen
        component: defaultPage,
        meta: { validate: ['is_auth','two_factor_security','is_screen_locked','subscription'] },
        children: [
            {
                path: '/subscription',
                component: require('@views/subscription/index').default
            }
        ]
    },
    {
        path: '/',
        component: () => import(/* webpackChunkName: "js/guestPage" */ '@layouts/guest-page'),
        meta: { validate: ['is_auth'] },
        children: [
            ...authSecurityRoutes,
        ]
    },
    {
        path: '/',
        component : require('@layouts/error-page').default,
        children: [
            ...errorRoutes
        ]
    }
];


const router = new VueRouter({
    routes,
    // linkActiveClass: 'active',
    mode: 'history',
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return { x: 0, y: 0 }
        }
    }
});

router.beforeEach((to, from, next) => {
    startNavProgress({ route: true });

    // Initialize toastr notification (only if jQuery available - helper uses $)
    try { helper.notification(); } catch (e) { /* ignore if jQuery not loaded */ }

    helper.setConfig().then(() => {

        let auth_token = helper.getAdminAuthToken();

        if(helper.getConfig('instance_pending') && to.fullPath != '/install') {
            return next({ path: '/install' })
        }

        // Check for authentication; If no, redirect to "/login" route
        if (auth_token && ! helper.hasRole('admin') && helper.getConfig('maintenance_mode') && to.fullPath != '/maintenance'){
            NioApp.Toast(helper.getConfig('maintenance_mode_message'), 'error');
            return next({ path: '/maintenance'})
        }

        if (to.matched.some(m => m.meta.validate)){
            const m = to.matched.find(m => m.meta.validate);

            // Check for authentication; If no, redirect to "/login" route
            if (m.meta.validate.indexOf('is_auth') > -1 && ! auth_token){
                helper.clearSession();
                (window.NioApp && window.NioApp.Toast) ? NioApp.Toast(i18n.auth.auth_required, 'error') : (window.toastr && toastr.error && toastr.error(i18n.auth.auth_required));
                return next({ path: '/login' })
            }

            // Check for two factor security; If enabled, redirect to "/auth/security" route after login
            if (to.fullPath != '/auth/security' && m.meta.validate.indexOf('two_factor_security') > -1 && helper.getConfig('two_factor_security') && helper.getAuthUser('two_factor_code')){
                return next({ path: '/auth/security' })
            }

            // Check for role; If no admin role, redirect to "/login" route
            if (m.meta.validate.indexOf('subscription') > -1){
                if(!helper.hasRole('admin')){
                    NioApp.Toast(i18n.general.permission_denied, 'error');
                    return next({ path: '/login' })
                }
            }

            // Check for subscription; If expired, redirect to "/subscription" route
            if (m.meta.validate.indexOf('valid_subscription') > -1){
                if(helper.getConfig('date_of_expiry') < moment().format('YYYY-MM-DD')){
                    NioApp.Toast(i18n.subscription.subscription_expired, 'error');
                    return next({ path: '/subscription' })
                }
            }

            // Check for valid academic session; If invalid redirect to home
            if (m.meta.validate.indexOf('has_academic_session') > -1 && ! helper.getDefaultAcademicSession()){
                NioApp.Toast(i18n.academic.choose_session_detail, 'error');
                return next({ path: '/home' })
            }

            // Check if frontend website is enabled; If invalid redirect to "/login"
            if (m.meta.validate.indexOf('is_frontend_website') > -1 && !helper.getConfig('frontend_website')){
                return next({ path: '/login' })
            }

            // Guest-only routes (login, install): clear stale session and allow access
            if (m.meta.validate.indexOf('is_guest') > -1 && auth_token){
                helper.clearSession();
                return next();
            }
        }
        return next()
    }).catch(error => {
        stopNavProgress();
        console.error('Router config error:', error);
        try {
            helper.showErrorMsg(error);
        } catch (e) { /* ignore */ }
        // next(false) aborts navigation and leaves an empty #root (blank screen). Still navigate so login can render.
        next();
    });
});

router.afterEach((to, from) => {
    stopNavProgress();
});

export default router;
